/**
 * CompanyTeam — production-company contact roster + default-CC routing.
 *
 * Lives at /settings/team. Operators add team members one at a time;
 * picking a role from a curated grouped dropdown (Producer / Director /
 * Booking Agent / Publicist / etc.) with an "Other (specify)" fallback
 * when nothing in the list fits.
 *
 * The cc_by_default flag on each row is the load-bearing one — every
 * outgoing email from the system on behalf of this production company
 * merges its cc_by_default=true contacts onto the CC line. So the
 * producer gets a copy of every delivery email automatically; the
 * operator doesn't have to remember.
 *
 * v10.20.9.
 */
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, Chip, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Checkbox, FormControlLabel, ListSubheader,
  Snackbar, Alert, CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import api from '../services/api';


const BLANK_FORM = {
  role: 'producer',
  role_other: '',
  name: '',
  email: '',
  phone: '',
  notes: '',
  is_primary: false,
  cc_by_default: false,
  position: 0,
};


export default function CompanyTeam() {
  const [contacts, setContacts] = useState(null); // null = loading
  const [roleOptions, setRoleOptions] = useState([]);
  const [editing, setEditing] = useState(null);    // contact id being edited, 'new' for create, null for closed
  const [form, setForm] = useState(BLANK_FORM);
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState(null);

  const loadContacts = async () => {
    try {
      const { data } = await api.get('/api/companies-context/contacts');
      setContacts(data || []);
    } catch (e) {
      setSnack({ severity: 'error', msg: e?.response?.data?.detail || 'Could not load contacts.' });
      setContacts([]);
    }
  };

  const loadRoles = async () => {
    try {
      const { data } = await api.get('/api/companies-context/contacts/roles');
      setRoleOptions(data || []);
    } catch (_e) {
      // Fall back to a tiny set so the dialog still opens
      setRoleOptions([
        { slug: 'producer', label: 'Producer', group: 'Production' },
        { slug: 'director', label: 'Director', group: 'Production' },
        { slug: 'other', label: 'Other (specify)', group: 'Other' },
      ]);
    }
  };

  useEffect(() => {
    loadContacts();
    loadRoles();
  }, []);

  // Group the role options for the dropdown subheaders.
  const groupedRoles = useMemo(() => {
    const groups = {};
    for (const opt of roleOptions) {
      if (!groups[opt.group]) groups[opt.group] = [];
      groups[opt.group].push(opt);
    }
    return groups;
  }, [roleOptions]);

  const roleLabel = (slug, roleOther) => {
    if (slug === 'other' && roleOther) return roleOther;
    const m = roleOptions.find((r) => r.slug === slug);
    return m ? m.label : slug;
  };

  // Group active contacts by role for the page display.
  const contactsByRole = useMemo(() => {
    if (!contacts) return {};
    const out = {};
    for (const c of contacts) {
      const key = c.role === 'other' && c.role_other ? `other::${c.role_other}` : c.role;
      if (!out[key]) out[key] = [];
      out[key].push(c);
    }
    return out;
  }, [contacts]);

  const openCreate = () => {
    setForm({ ...BLANK_FORM });
    setEditing('new');
  };

  const openEdit = (contact) => {
    setForm({
      role: contact.role,
      role_other: contact.role_other || '',
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      notes: contact.notes || '',
      is_primary: !!contact.is_primary,
      cc_by_default: !!contact.cc_by_default,
      position: contact.position || 0,
    });
    setEditing(contact.id);
  };

  const handleSave = async () => {
    if (!form.name && !form.email) {
      setSnack({ severity: 'warning', msg: 'Add at least a name or an email.' });
      return;
    }
    if (form.role === 'other' && !form.role_other.trim()) {
      setSnack({ severity: 'warning', msg: 'Custom role needs a label.' });
      return;
    }
    setBusy(true);
    try {
      const payload = {
        role: form.role,
        role_other: form.role === 'other' ? form.role_other.trim() : null,
        name: form.name.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        notes: form.notes.trim() || null,
        is_primary: !!form.is_primary,
        cc_by_default: !!form.cc_by_default,
        position: form.position,
      };
      if (editing === 'new') {
        await api.post('/api/companies-context/contacts', payload);
      } else {
        await api.patch(`/api/companies-context/contacts/${editing}`, payload);
      }
      setEditing(null);
      loadContacts();
      setSnack({ severity: 'success', msg: editing === 'new' ? 'Contact added.' : 'Contact updated.' });
    } catch (e) {
      setSnack({ severity: 'error', msg: e?.response?.data?.detail || 'Could not save contact.' });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`Remove ${contact.name || contact.email} from the team?`)) return;
    try {
      await api.delete(`/api/companies-context/contacts/${contact.id}`);
      loadContacts();
      setSnack({ severity: 'success', msg: 'Contact removed.' });
    } catch (e) {
      setSnack({ severity: 'error', msg: e?.response?.data?.detail || 'Could not remove contact.' });
    }
  };

  const ccByDefault = (contacts || []).filter(c => c.cc_by_default && c.email);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <PageHeader
        title="Team"
        subtitle="Producer, director, booking agent, publicist — whoever should know what's happening. Anyone marked CC by default rides along on every email the system sends on this company's behalf."
        actions={
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add contact
          </Button>
        }
      />

      {ccByDefault.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderColor: 'primary.main', bgcolor: 'action.selected' }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
            Default CC on every outgoing email ({ccByDefault.length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {ccByDefault.map((c) => (
              <Chip
                key={c.id}
                size="small"
                label={`${c.name || c.email} (${roleLabel(c.role, c.role_other)})`}
                onDelete={() => api.patch(`/api/companies-context/contacts/${c.id}`, { cc_by_default: false }).then(loadContacts)}
              />
            ))}
          </Box>
        </Paper>
      )}

      {contacts === null && <CircularProgress size={20} />}

      {contacts !== null && contacts.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 1 }}>No team members yet.</Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            Add the producer, director, booking agent, and anyone else who should see what's going out.
          </Typography>
        </Paper>
      )}

      {contacts !== null && contacts.length > 0 && Object.entries(contactsByRole).map(([roleKey, rows]) => {
        const first = rows[0];
        const headerLabel = roleLabel(first.role, first.role_other);
        return (
          <Paper key={roleKey} variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1, bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={600}>{headerLabel}</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>CC by default</TableCell>
                    <TableCell sx={{ width: 100 }} align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={c.is_primary ? 600 : 400}>
                          {c.name || <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>—</Box>}
                        </Typography>
                        {c.notes && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            {c.notes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{c.email || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{c.phone || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        {c.cc_by_default ? (
                          <Chip size="small" label="CC" color="primary" />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Remove"><IconButton size="small" onClick={() => handleDelete(c)}><DeleteIcon fontSize="small" color="error" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })}

      <Dialog open={!!editing} onClose={() => !busy && setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing === 'new' ? 'Add team contact' : 'Edit team contact'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                label="Role"
                value={form.role}
                onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}
              >
                {Object.entries(groupedRoles).flatMap(([group, opts]) => [
                  <ListSubheader key={`hdr-${group}`}>{group}</ListSubheader>,
                  ...opts.map((opt) => (
                    <MenuItem key={opt.slug} value={opt.slug}>{opt.label}</MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>
            {form.role === 'other' && (
              <TextField
                label="Custom role"
                value={form.role_other}
                onChange={(e) => setForm(f => ({ ...f, role_other: e.target.value }))}
                placeholder="e.g. Costume Designer"
                fullWidth
                size="small"
              />
            )}
            <TextField label="Name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} fullWidth size="small" />
            <TextField label="Email" type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} fullWidth size="small" />
            <TextField label="Phone (optional)" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} fullWidth size="small" />
            <TextField label="Notes (optional)" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} multiline minRows={2} maxRows={5} fullWidth size="small" />
            <FormControlLabel
              control={<Checkbox checked={form.cc_by_default} onChange={(_e, v) => setForm(f => ({ ...f, cc_by_default: v }))} />}
              label="CC on every outgoing email from the system"
            />
            <FormControlLabel
              control={<Checkbox checked={form.is_primary} onChange={(_e, v) => setForm(f => ({ ...f, is_primary: v }))} />}
              label="Primary contact for this role"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={busy}>
            {busy ? 'Saving…' : (editing === 'new' ? 'Add' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} sx={{ width: '100%' }}>
            {snack.msg}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  );
}
